/**
 * Our own shader sources (W2-01a preview-gl; ARCHITECTURE.md §7.3, rendering.md R§4.4, R§9).
 *
 * - `quad`: every textured or flat quad of the preview (line bodies, texture / GIF / text lines, note buckets,
 *   hit-effect sprites, particles, HUD, background). Fragment = texel x vertex colour, straight alpha (R§4.4:
 *   out.rgb = tint.rgb * texel.rgb, out.a = a * texel.a); flat quads sample a 1x1 white texture.
 * - `disc`: paint-line stamps, filled ellipses written PREMULTIPLIED into a persistent paint buffer.
 * - `copy`: full-target texture copies (present, MSAA hand-back) without blending.
 * - `EFFECT_VERTEX`: Phira's fixed effect vertex stage, GLSL ES 1.00 so it links with package shaders written in
 *   GLSL ES 1.00: `uv = (texcoord - 0.5) * UVScale + 0.5`, (0, 0) = bottom-left of the screen texture (R§9, P§5.2).
 *
 * Vertex layout shared by quad / disc / copy (20 bytes per vertex): position f32 x2, uv f32 x2, colour u8 x4
 * (normalised). The effect stage uses `position` (clip space) and `texcoord`.
 */

/** Attribute locations (bound before linking). */
export const ATTR = { pos: 0, uv: 1, color: 2 } as const;

export const QUAD_ATTRIBS: Readonly<Record<string, number>> = { a_pos: ATTR.pos, a_uv: ATTR.uv, a_color: ATTR.color };
export const EFFECT_ATTRIBS: Readonly<Record<string, number>> = { position: ATTR.pos, texcoord: ATTR.uv };

export const QUAD_VERTEX = `#version 300 es
precision highp float;
layout(location = 0) in vec2 a_pos;
layout(location = 1) in vec2 a_uv;
layout(location = 2) in vec4 a_color;
uniform mat3 u_xf;
out vec2 v_uv;
out vec4 v_color;
void main() {
  vec3 p = u_xf * vec3(a_pos, 1.0);
  gl_Position = vec4(p.xy, 0.0, 1.0);
  v_uv = a_uv;
  v_color = a_color;
}
`;

export const QUAD_FRAGMENT = `#version 300 es
precision highp float;
uniform sampler2D u_tex;
in vec2 v_uv;
in vec4 v_color;
out vec4 o_color;
void main() {
  o_color = texture(u_tex, v_uv) * v_color;
}
`;

/** Ellipse inscribed in the quad: uv runs -1..1 across it. Output premultiplied (paint buffers are layers). */
export const DISC_FRAGMENT = `#version 300 es
precision highp float;
in vec2 v_uv;
in vec4 v_color;
out vec4 o_color;
void main() {
  if (dot(v_uv, v_uv) > 1.0) discard;
  o_color = vec4(v_color.rgb * v_color.a, v_color.a);
}
`;

/** Plain texture copy (vertex colour ignored). */
export const COPY_FRAGMENT = `#version 300 es
precision highp float;
uniform sampler2D u_tex;
in vec2 v_uv;
in vec4 v_color;
out vec4 o_color;
void main() {
  o_color = texture(u_tex, v_uv);
}
`;

/** Phira's fixed effect vertex stage (GLSL ES 1.00; not overridable by packages). */
export const EFFECT_VERTEX = `attribute vec2 position;
attribute vec2 texcoord;
uniform vec2 UVScale;
varying vec2 uv;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
  uv = (texcoord - 0.5) * UVScale + 0.5;
}
`;
